import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function EmergencyServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEmergencyServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services?category=emergency`);
        setServices(response.data || []);
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setError('Failed to load emergency services');
        setLoading(false);
      }
    };

    fetchEmergencyServices();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">24/7 Emergency Pet Services</h1>
        <p className="text-xl text-gray-600">
          Find immediate care for your pets in San Francisco
        </p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-red-800 mb-2">Emergency Instructions</h2>
        <ul className="list-disc list-inside text-red-700 space-y-2">
          <li>Call ahead to confirm availability and prepare for your arrival</li>
          <li>Have your pet's medical history ready if available</li>
          <li>Keep calm and drive safely</li>
          <li>If possible, call someone to accompany you</li>
        </ul>
      </div>

      <div className="space-y-6">
        {services && services.map((service) => (
          <Link
            key={service.id}
            to={`/services/${service.id}`}
            className="block bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="p-6 relative">
              <span className="absolute top-4 right-4 bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                24/7 Emergency
              </span>
              <div className="mb-4">
                <img 
                  src={service.image_url || `https://placehold.co/800x400/eef2ff/4f46e5?text=🐾+${encodeURIComponent(service.business_name || 'Pet Service')}`} 
                  alt={service.business_name || 'Pet Service'}
                  className="w-full h-48 object-cover rounded-lg bg-blue-50"
                  onError={(e) => {
                    e.target.onerror = null; // Prevent infinite loop
                    e.target.src = `https://placehold.co/800x400/eef2ff/4f46e5?text=🐾+${encodeURIComponent(service.business_name || 'Pet Service')}`;
                  }}
                />
              </div>
              <div className="space-y-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">{service.business_name}</h3>
                  <p className="text-blue-600 font-medium">{service.business_name}</p>
                </div>

                <div className="flex items-center">
                  <span className="text-yellow-400 text-lg">{'★'.repeat(Math.floor(service.rating))}</span>
                  <span className="text-gray-200 text-lg">{'★'.repeat(5 - Math.floor(service.rating))}</span>
                  {service.reviews_count > 0 && (
                    <span className="text-gray-600 ml-2 text-sm">({service.reviews_count} reviews)</span>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                  <p className="text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {service.address.street}
                  </p>
                  <p className="text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {service.contact.phone}
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {service.petTypes && service.petTypes.map((pet) => (
                    <span
                      key={pet}
                      className="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-blue-50 text-blue-700"
                    >
                      {pet.charAt(0).toUpperCase() + pet.slice(1).replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Link>
        ))}

        {services.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600">No emergency services found at the moment.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default EmergencyServices; 